/* eslint-disable no-plusplus */
import { ListTable, Typography } from 'design_system/src';
import { ReactComponent as Down } from 'design_system/src/static/icons/down-md.svg';
import { ReactComponent as Up } from 'design_system/src/static/icons/up-md.svg';
import { getLoanAmortizationSchedule } from 'models/loan/controllers/loan.controller';
import { PaymentAgreementStatusValues } from 'models/loan/enums/loan.enum';
import { OrderStatusValues } from 'models/loan/enums/order.enum';
import { PaymentStatusValues } from 'models/loan/enums/payment.enum';
import {
	LoanWithExtraFields,
	PaymentAgreement,
} from 'models/loan/interfaces/loan.interface';
import { AmortizationScheduleRow } from 'models/loan/interfaces/order.interface';
import { Payment } from 'models/loan/interfaces/payment.interface';
import { formatCalendarDate, momentLocal } from 'models/shared/helpers/dates';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { priceFormatFinancial } from 'models/shared/helpers/prices';
import React, { useCallback, useEffect, useState } from 'react';
import { LightTooltip } from '../tooltip/Tooltip';
import './LoanPaymentsTable.scss';

interface Props {
	loan: LoanWithExtraFields;
}

interface ScheduleRow extends AmortizationScheduleRow {
	payments: Payment[];
	agreements: PaymentAgreement[];
}

const LoanPaymentsTable: React.FC<Props> = ({ loan }) => {
	const [orders, setOrders] = useState<ScheduleRow[]>([]);
	const [loadingSchedule, setLoadingSchedule] = useState(false);
	const [openOrder, setOpenOrder] = useState(-1);

	useEffect(() => {
		setLoadingSchedule(true);
		getLoanAmortizationSchedule(loan._id)
			.then((schedule) => {
				const rows = schedule
					.sort((rowA, rowB) => rowA.quota - rowB.quota)
					.map((row) => {
						const payments = loan.payments.filter(
							(payment) => payment.quota === row.quota,
						);
						const agreements =
							loan.paymentAgreements?.filter(
								(payment) => payment.quota === row.quota,
							) ?? [];
						return { ...row, payments, agreements };
					});
				setOrders(rows);
			})
			.catch(commonErrorHandler)
			.finally(() => setLoadingSchedule(false));
	}, [loan]);

	const renderChild = useCallback(
		(order: ScheduleRow, index: number) =>
			index === openOrder ? (
				<div className="bg_neutral_0 shadow_soft">
					<div className="display_flex bg_neutral_400 m_t_xxs p_y_sm">
						<Typography
							scale="small"
							weight="400"
							className="flex_grow_1 flex_basis_0 text_center"
						>
							Fecha
						</Typography>
						<Typography
							scale="small"
							weight="400"
							className="flex_grow_1 flex_basis_0 text_center"
						>
							Acción
						</Typography>
						<Typography
							scale="small"
							weight="400"
							className="flex_grow_1 flex_basis_0 text_center"
						>
							Monto
						</Typography>
					</div>
					{order.payments?.length
						? order.payments.map((payment) => (
								<div className="display_flex p_y_sm">
									<Typography
										scale="small"
										weight="600"
										className="flex_grow_1 flex_basis_0 text_center"
									>
										{momentLocal(payment.paidDate).format('DD/MM/YYYY')}
									</Typography>
									<div className="table_divider flex_grow_0 h_lg" />
									<Typography
										scale="small"
										weight="600"
										className="flex_grow_1 flex_basis_0 text_center"
									>
										{PaymentStatusValues[payment.status]}
									</Typography>
									<div className="table_divider flex_grow_0 h_lg" />
									<Typography
										scale="small"
										weight="600"
										className="flex_grow_1 flex_basis_0 text_center"
									>
										{priceFormatFinancial(payment.paidAmount)}
									</Typography>
								</div>
						  ))
						: null}

					{order.agreements?.length
						? order.agreements.map((agreement) => (
								<div className="display_flex p_y_sm">
									<Typography
										scale="small"
										weight="600"
										className="flex_grow_1 flex_basis_0 text_center"
									>
										{formatCalendarDate(agreement.dueDate)}
									</Typography>
									<div className="table_divider flex_grow_0 h_lg" />
									<Typography
										scale="small"
										weight="600"
										className={`flex_grow_1 flex_basis_0 text_center text_${
											PaymentAgreementStatusValues[agreement.status].color
										}`}
									>
										{PaymentAgreementStatusValues[agreement.status].title}
									</Typography>
									<div className="table_divider flex_grow_0 h_lg" />
									<Typography
										scale="small"
										weight="600"
										className="flex_grow_1 flex_basis_0 text_center"
									>
										{priceFormatFinancial(
											agreement.amount +
												(agreement.toLateInterest ?? 0) -
												(agreement.discount ?? 0),
										)}
									</Typography>
								</div>
						  ))
						: null}

					{!order.agreements?.length && !order.payments?.length ? (
						<Typography
							scale="small"
							weight="600"
							className="flex_grow_1 text_center p_y_xs"
						>
							No hay pagos ni acuerdos para esta orden
						</Typography>
					) : null}
				</div>
			) : (
				<div />
			),
		[openOrder],
	);

	if (loadingSchedule && !orders.length) {
		return (
			<Typography scale="medium" weight="400" className="p_md text_center">
				Cargando tabla de amortización...
			</Typography>
		);
	}

	return (
		<ListTable
			data={orders}
			canSelect={false}
			dividers
			headerClassName="bg_transparent text_xsmall_600"
			rowClassName="table_row_custom border_1 border_solid border_neutral_400"
			cols={[
				{
					label: '',
					weight: 0,
					render: (order: ScheduleRow) => (
						<div className="flex_center_col">
							<Typography scale="small" weight="600">
								{order.quota ?? '-'}
							</Typography>
						</div>
					),
				},
				{
					label: 'F.Vto',
					render: (order: ScheduleRow) => (
						<div className="flex_center_col">
							<Typography scale="small" weight="600">
								{formatCalendarDate(order.dueDate)}
							</Typography>
						</div>
					),
				},
				{
					label: 'F. Pago',
					render: (order: ScheduleRow) => (
						<div className="flex_center_col">
							<Typography scale="small" weight="600">
								{order?.payments[0]?.paidDate
									? momentLocal(order.payments[0].paidDate).format('DD/MM/YYYY')
									: '--'}
							</Typography>
						</div>
					),
				},
				{
					label: 'Capital',
					render: (order: ScheduleRow) => (
						<div className="flex_center_col">
							<Typography scale="small" weight="600" textColor="primary_300">
								{priceFormatFinancial(order.capital)}
							</Typography>
						</div>
					),
				},
				{
					label: 'I.Corrientes',
					render: (order: ScheduleRow) => (
						<Typography
							weight="400"
							scale="small"
							textColor="neutral_700"
							className="text_center flex_grow_1 flex_basis_0"
						>
							{priceFormatFinancial(order.interest)}
						</Typography>
					),
				},
				{
					label: 'Descuento',
					render: (order: ScheduleRow) => (
						<Typography
							weight="400"
							scale="small"
							textColor={order.discount ? 'green_300' : 'neutral_700'}
							className="text_center flex_grow_1 flex_basis_0"
						>
							{priceFormatFinancial(order.discount ?? 0)}
						</Typography>
					),
				},
				{
					label: 'Seguro',
					render: (order: ScheduleRow) => (
						<Typography
							weight="400"
							scale="small"
							textColor="neutral_700"
							className="text_center flex_grow_1 flex_basis_0"
						>
							{priceFormatFinancial(order.insurance)}
						</Typography>
					),
				},
				{
					label: 'GPS',
					render: (order: ScheduleRow) => (
						<Typography
							weight="400"
							scale="small"
							textColor="neutral_700"
							className="text_center flex_grow_1 flex_basis_0"
						>
							{priceFormatFinancial(order.gps)}
						</Typography>
					),
				},
				{
					label: 'Total',
					render: (order: ScheduleRow) => (
						<div className="flex_center_col">
							<Typography scale="small" weight="600" textColor="primary_300">
								{priceFormatFinancial(order.total)}
							</Typography>
						</div>
					),
				},
				{
					label: 'Estado',
					render: (order: ScheduleRow) => (
						<Typography
							weight="400"
							scale="small"
							textColor="neutral_700"
							className="text_center flex_grow_1 flex_basis_0"
						>
							<div
								className={`dso_chip_small w_fit center_x cardStatus bg_${
									OrderStatusValues[
										order.status as keyof typeof OrderStatusValues
									]?.color
								}_nocontrast text_neutral_0`}
							>
								{
									OrderStatusValues[
										order.status as keyof typeof OrderStatusValues
									]?.title
								}
							</div>
						</Typography>
					),
				},
				{
					label: '',
					weight: 0,
					render: (order: ScheduleRow, i: number) =>
						order.payments.length || order.agreements.length ? (
							<div className="flex_center flex_gap_md">
								<LightTooltip title="Detalle de los pagos">
									<div className="">
										{openOrder === i ? (
											<Up
												className="text_neutral_1000 dim_lg"
												onClick={() => setOpenOrder(-1)}
											/>
										) : (
											<Down
												className="text_neutral_1000 dim_lg"
												onClick={() => setOpenOrder(i)}
											/>
										)}
									</div>
								</LightTooltip>
							</div>
						) : (
							<div />
						),
				},
			]}
			child={renderChild}
		/>
	);
};

export default LoanPaymentsTable;
